/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { _isEmpty, _isNil, _notNil } from '@/littledash';
import type { FC } from 'react';
import { type CSSProperties, useEffect, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { flexRender } from '@tanstack/react-table';
import classNames from 'classnames';
import { DEFAULT_TABLE_WITH_CONTEXT_MIN_TABLE_HEIGHT } from '@/helpers.constants';
import TableHeader from './Cells/TableHeader';
import Pagination from './PaginationNew';
import {
  calculateWindowHeight,
  cellProps as cellPropsStyling,
  getTableWithContextConfig,
  headerProps,
} from './Table.utils';
import { useTableContext } from './TableContext';

interface TableWithContextProps {
  tableRef: React.RefObject<HTMLDivElement | null>;
  testIdPrefix?: string;
  maxHeight?: number | undefined;
}

const TableWithContext: FC<TableWithContextProps> = ({ tableRef, testIdPrefix, maxHeight }) => {
  const [rowWidth, setRowWidth] = useState<number>();
  const [tableWidth, setTableWidth] = useState<number>();
  const [headerHeight, setHeaderHeight] = useState<number>(0);
  const { rowHeight, maxTableHeight } = getTableWithContextConfig(maxHeight);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  const tableContext = useTableContext();
  const { table } = tableContext;

  const headerGroups = table.getHeaderGroups();
  const rows = table.getRowModel().rows;
  const pageCount = table.getPageCount();
  const { pageIndex, pageSize } = table.getState().pagination;
  const canPreviousPage = table.getCanPreviousPage();
  const canNextPage = table.getCanNextPage();
  const gotoPage = table.setPageIndex;
  const previousPage = table.previousPage;
  const nextPage = table.nextPage;
  const setPageSize = table.setPageSize;

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => rowHeight,
    overscan: 8,
  });

  // Calculate total columns width from table
  const totalColumnsWidth = headerGroups.reduce(
    (total, headerGroup) =>
      total + headerGroup.headers.reduce((groupTotal, header) => groupTotal + header.getSize(), 0),
    0
  );

  // Get additional props from context for backward compatibility
  const NoDataComponent = (tableContext as Record<string, unknown>).NoDataComponent;
  const pageSizes = ((tableContext as Record<string, unknown>).pageSizes as number[]) || [10, 25, 50, 100];
  const noDataMessage = ((tableContext as Record<string, unknown>).noDataMessage as string) || 'No rows to display';

  const _NoDataComponent = NoDataComponent instanceof Function ? NoDataComponent(tableContext) : NoDataComponent;

  useEffect(() => {
    const handleResize = () => {
      setTableWidth((tableRef?.current as HTMLDivElement)?.scrollWidth ?? totalColumnsWidth);
    };

    handleResize();
    window.addEventListener('resize', handleResize, {
      capture: false,
      passive: true,
    });
    return () => window.removeEventListener('resize', handleResize);
  }, [totalColumnsWidth, tableRef]);

  useEffect(() => {
    const currentWidth = totalColumnsWidth <= (tableWidth ?? 0) ? (tableWidth ?? totalColumnsWidth) : totalColumnsWidth;
    setRowWidth(currentWidth);
  }, [totalColumnsWidth, tableWidth]);

  useEffect(() => {
    const headerEl = headerRef.current;
    if (_isNil(headerEl)) {
      return;
    }
    setHeaderHeight(headerEl.getBoundingClientRect().height);
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (_notNil(entry)) {
        setHeaderHeight(entry.contentRect.height);
      }
    });
    observer.observe(headerEl);
    return () => observer.disconnect();
  }, [headerGroups.length]);

  const containerHeight = _isEmpty(rows)
    ? DEFAULT_TABLE_WITH_CONTEXT_MIN_TABLE_HEIGHT
    : calculateWindowHeight(rows.length, rowHeight, headerHeight, maxTableHeight);

  return (
    <>
      <div
        className="ui__table"
        data-testid={`${testIdPrefix}__api-table`}
        data-test-component="TableWithContext"
        data-test-element="container"
      >
        <div ref={scrollContainerRef} className="table overflow-auto" style={{ height: containerHeight }}>
          <div
            data-testid={`${testIdPrefix}__table-container`}
            data-width-set={_notNil(rowWidth) ? 'true' : 'false'}
            style={{ width: rowWidth ?? undefined }}
          >
            <div
              ref={headerRef}
              className="thead"
              data-test-element="header"
              data-testid={`${testIdPrefix}__api-table-header`}
            >
              {headerGroups.map((headerGroup, groupIndex) => (
                <div className="tr" key={`headerGroup_${groupIndex}`}>
                  {headerGroup.headers.map((header, index) => (
                    <TableHeader key={`header_${index}`} header={header} headerProps={headerProps} />
                  ))}
                </div>
              ))}
            </div>
            <div className="tbody" data-test-element="body" data-testid={`${testIdPrefix}__api-table-body`}>
              {!_isEmpty(rows) ? (
                <div style={{ height: rowVirtualizer.getTotalSize(), position: 'relative', width: '100%' }}>
                  {rowVirtualizer.getVirtualItems().map((virtualItem) => (
                    <RenderRow
                      key={virtualItem.key}
                      index={virtualItem.index}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: virtualItem.size,
                        transform: `translateY(${virtualItem.start}px)`,
                      }}
                    />
                  ))}
                </div>
              ) : (
                <>
                  {_NoDataComponent ? (
                    <div className="ma3">{_NoDataComponent}</div>
                  ) : (
                    <div className="flex flex-column white min-h5 justify-center items-center tc pa3 ma3 bg-light-gray">
                      <h2 className="tc pa3 ma3">{noDataMessage ?? 'No rows to display'}</h2>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {rows.length >= 1 && (
        <Pagination
          previousPage={previousPage}
          nextPage={nextPage}
          pageCount={pageCount}
          gotoPage={gotoPage}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          pageIndex={pageIndex}
          pageSize={pageSize}
          pageSizes={pageSizes}
          setPageSize={setPageSize}
          testPrefix={testIdPrefix}
        />
      )}
    </>
  );
};

const RenderRow = ({ index, style }: { index: number; style: CSSProperties }) => {
  const { table } = useTableContext();
  const page = table.getRowModel().rows;
  const row = page[index];
  const isCheckboxCell = (meta: Record<string, unknown> | undefined) =>
    (meta?.className as string)?.includes('ui__selector-column');

  return (
    <div
      style={style}
      className={classNames('tr', {
        'bb b--moon-gray': index === page.length - 1,
      })}
      role="row"
      data-test-component="RenderRow"
      data-test-element="row-container"
      data-test-key={String(index)}
      data-testid={`api-table-row-${index}`}
    >
      {row.getVisibleCells().map((cell, columnIndex) => {
        const cellStyle = cellPropsStyling({}, { cell });
        const meta = cell.column.columnDef.meta as Record<string, unknown>;

        return (
          <div
            className={classNames('td', meta?.className as string | undefined)}
            key={cell.id}
            role="cell"
            data-test-component="RenderRow"
            data-test-element="cell"
            data-test-key={`( ${cell.column.id} , ${cell.row.id} )`}
            style={{
              ...(!isCheckboxCell(meta) && {
                ...cellStyle[1]?.style,
                flex: `1 0 ${cell.column.getSize()}px`,
              }),
              minWidth: cell.column.columnDef.minSize,
              maxWidth:
                isCheckboxCell(meta) || (meta?.className as string)?.includes('ui__expander-column')
                  ? cell.column.getSize()
                  : undefined,
            }}
            data-testid={`api-table-column-${columnIndex}`}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </div>
        );
      })}
    </div>
  );
};

export default TableWithContext;
